import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AuthContext } from "./auth-context";
import {
  GetUserConversations,
  SendMessage,
  GetBuiltConversationById,
  isSupabaseBackend,
  subscribeToMessages,
  subscribeToConversations,
} from "../services/backend";

const POLL_INTERVAL_ACTIVE_MS = 2500;
const POLL_INTERVAL_LIST_MS = 8000;
const POLL_INTERVAL_HIDDEN_MS = 30000;
const PENDING_MATCH_WINDOW_MS = 30000;
const LAST_SEEN_STORAGE_PREFIX = "payinapo:msg-lastseen:";

export const MessagesContext = createContext({
  conversations: [],
  activeConversationId: "",
  setActiveConversationId: () => {},
  totalUnreadCount: 0,
  perConvUnreadCount: {},
  markConversationRead: () => {},
  sendMessage: async () => {},
  kickPoll: () => {},
  ensureConversationLoaded: async () => null,
});

function lastSeenStorageKey(userId) {
  return userId ? `${LAST_SEEN_STORAGE_PREFIX}${userId}` : null;
}

function loadLastSeen(userId) {
  const key = lastSeenStorageKey(userId);
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistLastSeen(userId, map) {
  const key = lastSeenStorageKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function getMessageTimeMs(msg) {
  if (!msg || typeof msg !== "object") return null;
  const raw =
    msg.timestamp ?? msg.createdAt ?? msg.sentAt ?? msg.date ?? msg.time ?? msg.messageTime;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") {
    const n = raw < 1e12 ? raw * 1000 : raw;
    return Number.isFinite(n) ? n : null;
  }
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

function computeReadWatermark(conv, nowMs = Date.now()) {
  let maxT = nowMs;
  if (!conv) return maxT;
  for (const msg of conv.messages || []) {
    if (!msg || msg._optimistic) continue;
    const t = getMessageTimeMs(msg);
    if (t != null && t > maxT) maxT = t;
  }
  if (conv.lastMessageTime) {
    const t = new Date(conv.lastMessageTime).getTime();
    if (Number.isFinite(t) && t > maxT) maxT = t;
  }
  return maxT;
}

export function MessagesProvider({ children }) {
  const authCtx = useContext(AuthContext);
  const userId =
    authCtx.authenticatedUser?.id ?? authCtx.authenticatedUser?.userId ?? null;

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationIdState] = useState("");
  const [lastSeenMap, setLastSeenMap] = useState({});

  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  const pollTimerRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const pollCancelledRef = useRef(false);
  const pendingSendsRef = useRef([]);
  const tickRef = useRef(() => {});
  const lastSeenLoadedRef = useRef(false);
  const activeConvRef = useRef("");
  const useRealtime = isSupabaseBackend();

  useEffect(() => {
    activeConvRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    lastSeenLoadedRef.current = false;
    if (!userId) {
      setLastSeenMap({});
      lastSeenLoadedRef.current = true;
      return;
    }
    setLastSeenMap(loadLastSeen(userId));
    lastSeenLoadedRef.current = true;
  }, [userId]);

  useEffect(() => {
    if (!lastSeenLoadedRef.current || !userId) return;
    persistLastSeen(userId, lastSeenMap);
  }, [userId, lastSeenMap]);

  const refreshConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await GetUserConversations(userId);
      setConversations(data || []);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    }
  }, [userId]);

  const upsertConversation = useCallback((built) => {
    if (!built?.conversationId) return;
    setConversations((prev) => {
      const without = (prev || []).filter((c) => c.conversationId !== built.conversationId);
      return [built, ...without];
    });
  }, []);

  /** Fetch a specific conversation into the inbox (match deep-link / both sides after match). */
  const ensureConversationLoaded = useCallback(
    async (conversationId) => {
      if (!conversationId || !userId) return null;
      const existing = conversationsRef.current.find((c) => c.conversationId === conversationId);
      if (existing) return existing;
      try {
        const built = await GetBuiltConversationById(conversationId, userId);
        if (built) upsertConversation(built);
        return built;
      } catch (error) {
        console.error("Failed to load conversation:", conversationId, error);
        return null;
      }
    },
    [userId, upsertConversation]
  );

  // Supabase Realtime — no polling
  useEffect(() => {
    if (!userId || !useRealtime) return undefined;

    refreshConversations();

    const msgChannel = subscribeToMessages((newMsg) => {
      setConversations((prev) => {
        const exists = prev.some((c) => c.conversationId === newMsg.conversation_id);
        if (!exists) {
          // New thread may arrive via message before list refresh — pull full list.
          void refreshConversations();
          return prev;
        }
        return prev.map((conv) => {
          if (conv.conversationId !== newMsg.conversation_id) return conv;
          const mapped = {
            conversationId: newMsg.conversation_id,
            senderId: newMsg.sender_id,
            content: newMsg.content,
            timestamp: newMsg.created_at,
          };
          return {
            ...conv,
            messages: [...(conv.messages || []), mapped],
            lastMessage: newMsg.content,
            lastMessageTime: newMsg.created_at,
          };
        });
      });
    });

    const convChannel = subscribeToConversations(userId, refreshConversations);

    const onChanged = (event) => {
      const convId = event?.detail?.conversationId;
      if (convId) {
        void ensureConversationLoaded(convId).then(() => refreshConversations());
      } else {
        refreshConversations();
      }
    };
    window.addEventListener("payinapo:conversations-changed", onChanged);

    return () => {
      msgChannel?.unsubscribe?.();
      convChannel?.unsubscribe?.();
      window.removeEventListener("payinapo:conversations-changed", onChanged);
    };
  }, [userId, useRealtime, refreshConversations, ensureConversationLoaded]);

  // Azure legacy polling
  useEffect(() => {
    if (!userId || useRealtime) return undefined;

    pollCancelledRef.current = false;

    const intervalFor = () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return POLL_INTERVAL_HIDDEN_MS;
      }
      return activeConvRef.current ? POLL_INTERVAL_ACTIVE_MS : POLL_INTERVAL_LIST_MS;
    };

    const reconcileWithPending = (serverData) => {
      const now = Date.now();
      pendingSendsRef.current = pendingSendsRef.current.filter(
        (p) => now - p.sentAt < PENDING_MATCH_WINDOW_MS
      );
      if (pendingSendsRef.current.length === 0) return serverData;

      const stillPending = [];
      const result = serverData.map((conv) => {
        const convPendings = pendingSendsRef.current.filter(
          (p) => p.conversationId === conv.conversationId
        );
        if (convPendings.length === 0) return conv;

        const serverMessages = conv.messages || [];
        const unmatched = convPendings.filter(
          (p) => !serverMessages.some((m) => m.senderId === p.senderId && m.content === p.content)
        );
        stillPending.push(...unmatched);
        if (unmatched.length === 0) return conv;

        const merged = [
          ...serverMessages,
          ...unmatched.map((p) => ({
            conversationId: p.conversationId,
            senderId: p.senderId,
            content: p.content,
            timestamp: new Date(p.sentAt).toISOString(),
            _optimistic: true,
          })),
        ];
        const tail = unmatched[unmatched.length - 1];
        return {
          ...conv,
          messages: merged,
          lastMessage: tail.content,
          lastMessageTime: new Date(tail.sentAt),
        };
      });

      pendingSendsRef.current = stillPending;
      return result;
    };

    const schedule = () => {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = setTimeout(tick, intervalFor());
    };

    const tick = async () => {
      if (pollCancelledRef.current) return;
      if (pollInFlightRef.current) {
        schedule();
        return;
      }
      pollInFlightRef.current = true;
      try {
        const data = await GetUserConversations(userId);
        if (pollCancelledRef.current) return;
        setConversations(reconcileWithPending(data || []));
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
      } finally {
        pollInFlightRef.current = false;
      }
      if (!pollCancelledRef.current) schedule();
    };

    tickRef.current = tick;

    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        clearTimeout(pollTimerRef.current);
        tick();
      }
    };

    const onConversationsChanged = () => {
      clearTimeout(pollTimerRef.current);
      tick();
    };

    tick();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("payinapo:conversations-changed", onConversationsChanged);

    return () => {
      pollCancelledRef.current = true;
      clearTimeout(pollTimerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("payinapo:conversations-changed", onConversationsChanged);
      tickRef.current = () => {};
    };
  }, [userId, useRealtime]);

  const markConversationRead = useCallback((conversationId) => {
    if (!conversationId) return;
    const conv = conversationsRef.current.find((c) => c.conversationId === conversationId);
    const w = computeReadWatermark(conv);
    setLastSeenMap((prev) => ({
      ...prev,
      [conversationId]: Math.max(prev[conversationId] ?? 0, w),
    }));
  }, []);

  const setActiveConversationId = useCallback((nextId) => {
    setActiveConversationIdState((prevId) => {
      const convs = conversationsRef.current;
      const w = (cid) => computeReadWatermark(convs.find((c) => c.conversationId === cid));

      setLastSeenMap((prevMap) => {
        const next = { ...prevMap };
        if (prevId && prevId !== nextId) {
          next[prevId] = Math.max(prevMap[prevId] ?? 0, w(prevId));
        }
        if (nextId) {
          next[nextId] = Math.max(prevMap[nextId] ?? 0, w(nextId));
        }
        return next;
      });

      return nextId;
    });
  }, []);

  const sendMessage = useCallback(
    async (convId, content) => {
      if (!convId || !userId || !content) return;
      const sentAt = Date.now();
      const optimisticMessage = {
        conversationId: convId,
        senderId: userId,
        content,
        timestamp: new Date(sentAt).toISOString(),
        _optimistic: true,
      };

      if (!useRealtime) {
        pendingSendsRef.current = [
          ...pendingSendsRef.current,
          { conversationId: convId, senderId: userId, content, sentAt },
        ];
      }

      setConversations((prev) =>
        prev.map((c) =>
          c.conversationId === convId
            ? {
                ...c,
                messages: [...(c.messages || []), optimisticMessage],
                lastMessage: content,
                lastMessageTime: new Date(sentAt),
              }
            : c
        )
      );

      setLastSeenMap((prev) => {
        const conv = conversationsRef.current.find((c) => c.conversationId === convId);
        const w = Math.max(computeReadWatermark(conv), sentAt);
        return { ...prev, [convId]: Math.max(prev[convId] ?? 0, w) };
      });

      try {
        await SendMessage({ conversationId: convId, senderId: userId, content });
        if (useRealtime) {
          setConversations((prev) =>
            prev.map((c) => {
              if (c.conversationId !== convId) return c;
              return {
                ...c,
                messages: (c.messages || []).filter((m) => m !== optimisticMessage),
              };
            })
          );
        }
      } catch (error) {
        if (!useRealtime) {
          pendingSendsRef.current = pendingSendsRef.current.filter(
            (p) => !(p.conversationId === convId && p.sentAt === sentAt && p.content === content)
          );
        }
        setConversations((prev) =>
          prev.map((c) => {
            if (c.conversationId !== convId) return c;
            return {
              ...c,
              messages: (c.messages || []).filter((m) => m !== optimisticMessage),
            };
          })
        );
        throw error;
      }

      if (!useRealtime) tickRef.current?.();
    },
    [userId, useRealtime]
  );

  const kickPoll = useCallback(() => {
    if (useRealtime) {
      refreshConversations();
    } else {
      tickRef.current?.();
    }
  }, [useRealtime, refreshConversations]);

  const perConvUnreadCount = useMemo(() => {
    const map = {};
    const myId = userId != null ? String(userId) : "";

    for (const conv of conversations) {
      const convId = conv.conversationId;
      if (!convId) continue;
      if (convId === activeConversationId) {
        map[convId] = 0;
        continue;
      }

      const lastSeenTs = lastSeenMap[convId] ?? 0;
      const messages = conv.messages || [];
      let count = 0;

      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        if (!msg || msg._optimistic) continue;
        const sender = msg.senderId != null ? String(msg.senderId) : "";
        if (myId && sender === myId) continue;
        if (msg.isRead === true || msg.read === true) continue;

        let msgTs = getMessageTimeMs(msg);
        if (msgTs == null && i === messages.length - 1 && conv.lastMessageTime) {
          const fallback = new Date(conv.lastMessageTime).getTime();
          if (Number.isFinite(fallback)) msgTs = fallback;
        }
        if (msgTs == null) continue;
        if (msgTs > lastSeenTs) count += 1;
      }

      if (count === 0 && messages.length > 0) {
        const last = messages[messages.length - 1];
        if (last && !last._optimistic) {
          const lastSender = last.senderId != null ? String(last.senderId) : "";
          if (myId && lastSender !== myId && last.isRead !== true && last.read !== true) {
            const tLast =
              getMessageTimeMs(last) ??
              (conv.lastMessageTime ? new Date(conv.lastMessageTime).getTime() : null);
            if (tLast != null && tLast > lastSeenTs) count = 1;
          }
        }
      }

      map[convId] = count;
    }
    return map;
  }, [conversations, lastSeenMap, activeConversationId, userId]);

  const totalUnreadCount = useMemo(
    () => Object.values(perConvUnreadCount).reduce((a, b) => a + b, 0),
    [perConvUnreadCount]
  );

  const value = useMemo(
    () => ({
      conversations,
      activeConversationId,
      setActiveConversationId,
      totalUnreadCount,
      perConvUnreadCount,
      markConversationRead,
      sendMessage,
      kickPoll,
      ensureConversationLoaded,
    }),
    [
      conversations,
      activeConversationId,
      setActiveConversationId,
      totalUnreadCount,
      perConvUnreadCount,
      markConversationRead,
      sendMessage,
      kickPoll,
      ensureConversationLoaded,
    ]
  );

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
}

export function useMessages() {
  return useContext(MessagesContext);
}

export default MessagesProvider;
